<template>
  <div class="snake-map-dashboard">
    <PageLoader :storage="appLoading" />
    <!-- ENGAGING ANALYTICS DATA LOADER OVERLAY -->
    <transition name="fade">
      <div v-if="appLoading" class="analytics-loading-overlay">
        <div class="analytics-loader-card">
          <!-- Radar Pulse animation -->
          <div class="radar-pulse-container">
            <div class="radar-ring ring-1"></div>
            <div class="radar-ring ring-2"></div>
            <div class="radar-ring ring-3"></div>
            <div class="loader-center-icon">
              <v-icon large color="#3f51b5">mdi-map-marker-radius-outline</v-icon>
            </div>
          </div>

          <h3 class="loader-heading text-indigo font-weight-black">INITIALIZING GEOSPATIAL ANALYTICS</h3>
          <p class="loader-subtext">Fetching & indexing historical SARPA rescue encounters...</p>

          <!-- Animated Progress Line -->
          <div class="loader-progress-wrap">
            <div class="loader-progress-bar-officer"></div>
          </div>

          <!-- Dynamic Rotating Status Messages -->
          <div class="loader-status-ticker">
            <v-icon x-small color="#3f51b5" class="mr-1 spinning-icon">mdi-loading</v-icon>
            <span class="ticker-text">{{ currentLoadingMessage }}</span>
          </div>

          <div class="loader-footer-hint">
            <v-icon x-small color="#64748b" class="mr-1">mdi-database-sync-outline</v-icon>
            Optimizing Leaflet Canvas Engine for 80,000+ records
          </div>
        </div>
      </div>
    </transition>
    <ServerError v-if="ServerError" />

    <v-snackbar v-model="showSnackBar" color="primary" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span class="white--text">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon color="#fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- Map Element -->
    <div id="map" ref="mapContainer"></div>

    <!-- Floating Title Banner -->
    <div class="floating-title-banner hidden-xs-only">
      <div class="banner-main-title">GEOSPATIAL & AI PREDICTIVE ANALYTICS</div>
      <div class="banner-sub-info">
        <span class="banner-subtitle">Snake Reports & Rescues</span>
        <v-chip x-small color="indigo" class="white--text font-weight-black ml-2" label>
          Loaded: {{ totalcount }}
        </v-chip>
      </div>
    </div>

    <!-- Floating Compass -->
    <div class="floating-north-arrow">
      <v-btn fab small color="white" depressed readonly style="pointer-events: none;">
        <v-icon color="indigo">mdi-navigation</v-icon>
      </v-btn>
    </div>

    <!-- Floating Scarcity Map Action Button -->
    <div class="floating-scarcity-btn-container hidden-xs-only">
      <v-btn
        :color="showShortageMapOverlay ? 'red darken-2' : 'white'"
        :class="showShortageMapOverlay ? 'white--text elevation-8 pulsate-scarcity' : 'red--text text--darken-2 elevation-4'"
        rounded
        small
        class="font-weight-black"
        @click="toggleShortageMapOverlay"
      >
        <v-icon left :color="showShortageMapOverlay ? 'white' : 'red darken-2'">
          {{ showShortageMapOverlay ? 'mdi-map-marker-alert' : 'mdi-account-alert-outline' }}
        </v-icon>
        {{ showShortageMapOverlay ? 'HIDE SCARCITY ZONES' : 'PREDICT RESCUER SCARCITY' }}
      </v-btn>
    </div>

    <!-- Left Vertical Floating Toolbar -->
    <div class="map-floating-toolbar">
      <v-btn
        fab
        small
        :color="activePanel === 'filter' ? 'indigo' : 'white'"
        class="toolbar-btn"
        @click="togglePanel('filter')"
        title="Query Filters"
      >
        <v-icon :color="activePanel === 'filter' ? 'white' : 'indigo'">mdi-filter-menu-outline</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        :color="activePanel === 'layers' ? 'indigo' : 'white'"
        class="toolbar-btn"
        @click="togglePanel('layers')"
        title="Map Overlays & GIS Layers"
      >
        <v-icon :color="activePanel === 'layers' ? 'white' : 'indigo'">mdi-layers-outline</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        :color="activePanel === 'predictive' ? 'indigo' : 'white'"
        class="toolbar-btn"
        @click="togglePanel('predictive')"
        title="AI Predictive Analytics"
      >
        <v-icon :color="activePanel === 'predictive' ? 'white' : 'indigo'">mdi-robot</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        :color="activePanel === 'ask_ai' ? 'indigo' : 'white'"
        class="toolbar-btn"
        @click="togglePanel('ask_ai')"
        title="Ask SARPA AI Assistant"
      >
        <v-icon :color="activePanel === 'ask_ai' ? 'white' : 'indigo'">mdi-head-question-outline</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        color="white"
        class="toolbar-btn"
        @click="exportMapPDF"
        :loading="exportLoading"
        title="Export Map as PDF"
      >
        <v-icon color="#e11d48">mdi-file-pdf-box</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        color="white"
        class="toolbar-btn"
        @click="exportMapPNG"
        :loading="exportLoading"
        title="Export Map as PNG"
      >
        <v-icon color="#2563eb">mdi-file-image-outline</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        :color="activePanel === 'stats' ? 'indigo' : 'white'"
        class="toolbar-btn"
        @click="togglePanel('stats')"
        title="Spatial Stats"
      >
        <v-icon :color="activePanel === 'stats' ? 'white' : 'indigo'">mdi-chart-box-outline</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        :color="showDrawControls ? 'indigo' : 'white'"
        class="toolbar-btn"
        @click="toggleDrawControls"
        title="Toggle Draw Tools"
      >
        <v-icon :color="showDrawControls ? 'white' : 'indigo'">mdi-vector-polygon</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        color="white"
        class="toolbar-btn"
        @click="toggleFullscreen"
        title="Fullscreen Mode"
      >
        <v-icon color="indigo">{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
      </v-btn>
    </div>

    <!-- Floating Panels Container -->
    <transition name="slide-fade">
      <!-- Filter Panel Overlay -->
      <v-card v-if="activePanel === 'filter'" key="card-filter" class="floating-overlay-card filter-card-overlay" elevation="8">
        <div class="panel-card-header d-flex justify-space-between align-center mb-4">
          <div>
            <h3 class="panel-card-title indigo--text text--darken-3">Query Criteria</h3>
            <p class="panel-card-subtitle ma-0">Filter snake incidents by categories and locations.</p>
          </div>
          <v-btn icon small @click="activePanel = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="panel-card-content">
          <div class="checkbox-grid mb-3">
            <v-checkbox v-model="alldata" label="All Records" @change="checkUpdate()" color="indigo" hide-details dense class="ma-0 mb-1"></v-checkbox>
            <v-checkbox v-model="timeperiod" label="Time Period" :disabled="alldata" color="indigo" hide-details dense class="ma-0 mb-1"></v-checkbox>
            <v-checkbox v-model="bigsnakes" label="Big 4 Snakes" :disabled="alldata" color="indigo" hide-details dense class="ma-0 mb-1"></v-checkbox>
            <v-checkbox v-model="allsnakes" label="All Species" :disabled="alldata" color="indigo" hide-details dense class="ma-0 mb-1"></v-checkbox>
            <v-checkbox v-model="users" label="Rescuer User" :disabled="alldata" color="indigo" hide-details dense class="ma-0 mb-1"></v-checkbox>
            <v-checkbox v-model="divisioncheck" label="Office (Div/Range)" :disabled="alldata" color="indigo" hide-details dense class="ma-0 mb-1"></v-checkbox>
            <v-checkbox v-model="districtcheck" label="District" :disabled="alldata" color="indigo" hide-details dense class="ma-0 mb-1"></v-checkbox>
          </div>

          <v-divider class="mb-3"></v-divider>

          <div class="filters-form-scroll" v-if="!alldata">
            <div class="mb-3" v-if="timeperiod">
              <span class="field-label mb-1 d-block font-weight-bold">From Date</span>
              <v-menu v-model="menu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="fromdate" append-icon="mdi-calendar" readonly outlined dense hide-details v-bind="attrs" v-on="on" color="indigo"></v-text-field>
                </template>
                <v-date-picker v-model="fromdate" @change="save" color="indigo"></v-date-picker>
              </v-menu>
            </div>

            <div class="mb-3" v-if="timeperiod">
              <span class="field-label mb-1 d-block font-weight-bold">To Date</span>
              <v-menu v-model="menu2" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="todate" append-icon="mdi-calendar" readonly outlined dense hide-details v-bind="attrs" v-on="on" color="indigo"></v-text-field>
                </template>
                <v-date-picker v-model="todate" @change="saveto" color="indigo"></v-date-picker>
              </v-menu>
            </div>

            <div class="mb-3" v-if="bigsnakes">
              <span class="field-label mb-1 d-block font-weight-bold">Select Big 4</span>
              <v-select :items="bigsnakestypes" v-model="bigsnakeselect" item-text="text" item-value="value" multiple outlined dense hide-details color="indigo"></v-select>
            </div>

            <div class="mb-3" v-if="allsnakes">
              <span class="field-label mb-1 d-block font-weight-bold">Select Species</span>
              <v-select :items="snakeitems" v-model="allsnakelist" multiple item-text="name" item-value="_id" outlined dense hide-details color="indigo"></v-select>
            </div>

            <div class="mb-3" v-if="users">
              <span class="field-label mb-1 d-block font-weight-bold">Select User</span>
              <v-autocomplete :items="userslist" v-model="selecteduser" :search-input.sync="searchUser" item-text="username" item-value="username" clearable outlined dense hide-details color="indigo"></v-autocomplete>
            </div>

            <div class="mb-3" v-if="divisioncheck">
              <span class="field-label mb-1 d-block font-weight-bold">Division</span>
              <v-autocomplete outlined :items="divisionList" v-model="divisionname" item-text="divisionName" item-value="_id" dense hide-details color="indigo"></v-autocomplete>
            </div>

            <div class="mb-3" v-if="divisioncheck">
              <span class="field-label mb-1 d-block font-weight-bold">Range</span>
              <v-autocomplete outlined :items="rangeList" v-model="rangename" item-text="rangeName" item-value="_id" dense hide-details color="indigo" :disabled="!divisionname"></v-autocomplete>
            </div>

            <div class="mb-3" v-if="districtcheck">
              <span class="field-label mb-1 d-block font-weight-bold">District</span>
              <v-select :items="districtList" v-model="district" outlined dense hide-details color="indigo"></v-select>
            </div>
          </div>

          <div v-else class="alldata-banner pa-3 mb-4 text-center rounded-lg">
            <v-icon small color="indigo" class="mr-1">mdi-information-outline</v-icon>
            <span class="caption indigo--text text--darken-3 font-weight-bold">Displaying all global records. No filters active.</span>
          </div>

          <div class="d-flex align-center gap-2 mt-2 pt-2">
            <v-btn v-if="!alldata" class="flex-grow-1 white--text" color="indigo" depressed @click="find()">Submit</v-btn>
            <v-btn v-if="!alldata" class="flex-grow-1" outlined color="indigo" depressed @click="reset()">Reset</v-btn>
          </div>
        </div>
      </v-card>
    </transition>

    <transition name="slide-fade">
      <!-- Layers & GIS Overlays Card -->
      <v-card v-if="activePanel === 'layers'" key="card-layers" class="floating-overlay-card layers-card-overlay" elevation="8">
        <div class="panel-card-header d-flex justify-space-between align-center mb-4">
          <div>
            <h3 class="panel-card-title indigo--text text--darken-3">Map Overlays & GIS Layers</h3>
            <p class="panel-card-subtitle ma-0">Configure data visualization & spatial boundary layers.</p>
          </div>
          <v-btn icon small @click="activePanel = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="panel-card-content">
          <div class="stats-label mb-2 font-weight-bold text-uppercase indigo--text text--darken-4" style="font-size:11px;">Data Displays</div>
          <div class="layer-option-row pa-2 mb-2 d-flex align-center rounded-lg">
            <v-checkbox v-model="showPointers" label="Individual Pointers" color="indigo" hide-details dense class="ma-0 pa-0" @change="toggleMapMode"></v-checkbox>
          </div>
          <div class="layer-option-row pa-2 mb-2 d-flex align-center rounded-lg">
            <v-checkbox v-model="showHotspots" label="Cluster Hotspots" color="indigo" hide-details dense class="ma-0 pa-0" @change="toggleMapMode"></v-checkbox>
          </div>
          <div class="layer-option-row pa-2 mb-3 d-flex align-center rounded-lg">
            <v-checkbox v-model="showHeatmap" label="Predictive Risk Heatmap" color="#d97706" hide-details dense class="ma-0 pa-0" @change="toggleMapMode"></v-checkbox>
          </div>

          <v-divider class="my-3"></v-divider>

          <div class="stats-label mb-2 font-weight-bold text-uppercase indigo--text text--darken-4" style="font-size:11px;">GIS Boundary Layers</div>
          <div class="layer-option-row pa-2 mb-2 d-flex align-center rounded-lg">
            <v-checkbox v-model="gisLayers.districts" label="District Boundaries" color="indigo" hide-details dense class="ma-0 pa-0" @change="toggleDistrictLayer"></v-checkbox>
          </div>
          <div class="layer-option-row pa-2 mb-2 d-flex align-center rounded-lg">
            <v-checkbox v-model="gisLayers.divisions" label="Forest Divisions" color="indigo" hide-details dense class="ma-0 pa-0" @change="toggleDivisionLayer"></v-checkbox>
          </div>
          <div class="layer-option-row pa-2 mb-2 d-flex align-center rounded-lg">
            <v-checkbox v-model="gisLayers.ranges" label="Forest Ranges" color="indigo" hide-details dense class="ma-0 pa-0" @change="toggleRangeLayer"></v-checkbox>
          </div>
          <div class="layer-option-row pa-2 mb-2 d-flex align-center rounded-lg">
            <v-checkbox v-model="gisLayers.pas" label="Protected Areas (PAs)" color="indigo" hide-details dense class="ma-0 pa-0" @change="togglePASLayer"></v-checkbox>
          </div>
        </div>
      </v-card>
    </transition>

    <transition name="slide-fade">
      <!-- ASK SARPA AI ASSISTANT CARD -->
      <v-card v-if="activePanel === 'ask_ai'" key="card-ask-ai" class="floating-overlay-card ask-ai-card-overlay" elevation="8">
        <div class="panel-card-header d-flex justify-space-between align-center mb-3">
          <div>
            <h3 class="panel-card-title indigo--text text--darken-3 d-flex align-center">
              <v-icon color="indigo" class="mr-1">mdi-head-question-outline</v-icon>
              Ask SARPA AI Assistant
            </h3>
            <p class="panel-card-subtitle ma-0">District Spatial Intelligence & Rescuer Allocation Query.</p>
          </div>
          <v-btn icon small @click="activePanel = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="panel-card-content stats-scroll-container">
          <!-- QUICK QUERY CHIPS -->
          <div class="caption font-weight-black grey--text text--darken-3 mb-1">Quick District Intelligence Query</div>
          <div class="d-flex flex-wrap gap-1 mb-3">
            <v-chip
              v-for="dName in [
                'Thiruvananthapuram', 'Kollam', 'Pathanamthitta', 'Alappuzha',
                'Kottayam', 'Idukki', 'Ernakulam', 'Thrissur', 'Palakkad',
                'Malappuram', 'Kozhikode', 'Wayanad', 'Kannur', 'Kasaragod'
              ]"
              :key="'chip-' + dName"
              x-small
              outlined
              color="indigo"
              class="font-weight-bold ma-1"
              @click="runDistrictAIQuery(dName)"
            >
              {{ dName }}
            </v-chip>
          </div>

          <!-- QUERY INPUT FIELD -->
          <v-text-field
            v-model="aiQueryInputText"
            placeholder="Ask a question (e.g. In Idukki do we need more rescuers?)"
            outlined
            dense
            hide-details
            append-icon="mdi-send"
            color="indigo"
            class="mb-3"
            @keyup.enter="handleAIQuerySubmit"
            @click:append="handleAIQuerySubmit"
          ></v-text-field>

          <!-- QUERY RESULT DISPLAY -->
          <div v-if="aiQueryResult" class="ai-query-result-box pa-3 rounded-lg" style="background: #eef2ff; border: 1px solid #c7d2fe;">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="font-weight-black text-uppercase caption indigo--text text--darken-4">
                📍 {{ aiQueryResult.districtName }} Intelligence Report
              </span>
              <v-chip x-small :color="aiQueryResult.shortageNeeded > 0 ? 'red darken-2' : 'indigo darken-2'" class="white--text font-weight-black">
                {{ aiQueryResult.shortageStatus }}
              </v-chip>
            </div>

            <!-- RESCUER RECOMMENDATION & EXPLANATION -->
            <div class="pa-2 rounded mb-3" style="background: #ffffff; border: 1px solid #e0e7ff;">
              <div class="caption font-weight-black text-uppercase grey--text text--darken-3 mb-1">
                🚨 Rescuer Deployment Need
              </div>
              <p class="caption font-weight-bold mb-2" style="color: #3f51b5;">
                {{ aiQueryResult.rescuerAdvice }}
              </p>

              <!-- SLA & BACKLOG METRICS STRIP -->
              <div class="d-flex mb-2" style="gap: 6px;">
                <div class="pa-2 rounded flex-grow-1" style="background: #fdf2f8; border: 1px solid #fbcfe8;">
                  <div class="caption grey--text text--darken-2" style="font-size: 10px;">Avg Response SLA</div>
                  <div class="font-weight-black text-truncate" style="font-size: 11px; color: #be185d;">
                    ⏱️ {{ aiQueryResult.avgResponseMins }} Mins Arrival
                  </div>
                </div>
                <div class="pa-2 rounded flex-grow-1" style="background: #fff7ed; border: 1px solid #ffedd5;">
                  <div class="caption grey--text text--darken-2" style="font-size: 10px;">Unattended Backlog</div>
                  <div class="font-weight-black text-truncate" style="font-size: 11px; color: #c2410c;">
                    ⚠️ {{ aiQueryResult.unattendedCount }} Pending ({{ aiQueryResult.unattendedRate }}%)
                  </div>
                </div>
              </div>

              <!-- WHY THIS RECOMMENDATION WAS GENERATED -->
              <div class="pa-2 rounded" style="background: #f8fafc; border-left: 3px solid #3f51b5; font-size: 11px;">
                <div class="font-weight-black grey--text text--darken-4 mb-1 d-flex align-center">
                  <v-icon small color="#3f51b5" class="mr-1">mdi-information-outline</v-icon>
                  Why this recommendation was displayed:
                </div>
                <div class="caption grey--text text--darken-3">
                  <div>• <strong>Encounter Volume:</strong> {{ aiQueryResult.totalCalls.toLocaleString() }} total rescue calls logged in {{ aiQueryResult.districtName }}.</div>
                  <div>• <strong>State Load Concentration:</strong> Represents <strong>{{ aiQueryResult.stateShare }}%</strong> of Kerala's total encounter workload.</div>
                  <div>• <strong>Response Time SLA:</strong> Average arrival time is <strong>{{ aiQueryResult.avgResponseMins }} mins</strong> ({{ aiQueryResult.slaStatus }}).</div>
                  <div>• <strong>Unattended Backlog Index:</strong> <strong>{{ aiQueryResult.unattendedCount.toLocaleString() }} calls</strong> currently pending rescuer response ({{ aiQueryResult.unattendedRate }}% backlog).</div>
                  <div>• <strong>Venom Threat Level:</strong> <strong>{{ aiQueryResult.venomPercent }}%</strong> of encounters involve Highly Venomous species (Cobra/Viper).</div>
                  <div>• <strong>Deployment Formula Derivation:</strong> {{ aiQueryResult.rescuerFormula }}</div>
                </div>
              </div>
            </div>

            <!-- SPECIES PROBABILITY CHANCE BREAKDOWN -->
            <div class="caption font-weight-black grey--text text--darken-3 mb-1">
              🐍 Species Encounter Chances in {{ aiQueryResult.districtName }}
            </div>
            <div v-for="sp in aiQueryResult.speciesChances" :key="'ai-sp-' + sp.name" class="mb-2">
              <div class="d-flex justify-space-between caption font-weight-bold" style="font-size: 10px;">
                <span>{{ sp.name }}</span>
                <span class="indigo--text text--darken-3 font-weight-black">{{ sp.chance }}% Chance</span>
              </div>
              <v-progress-linear :value="sp.chance" height="6" rounded color="indigo"></v-progress-linear>
            </div>

            <!-- SUB-DISTRICT AREA PREDICTION & RESCUER NEED BREAKDOWN -->
            <div v-if="aiQueryResult.districtAreasBreakdown && aiQueryResult.districtAreasBreakdown.length > 0" class="mt-3">
              <div class="caption font-weight-black grey--text text--darken-3 mb-1">
                📍 Predicted High-Risk Areas inside {{ aiQueryResult.districtName }}
              </div>
              <div
                v-for="area in aiQueryResult.districtAreasBreakdown"
                :key="'ai-area-' + area.areaName"
                class="pa-2 rounded mb-2"
                style="background: #f8fafc; border: 1px solid #cbd5e1;"
              >
                <div class="d-flex justify-space-between align-center">
                  <div class="font-weight-black text-truncate" style="font-size: 11px; color: #3f51b5; max-width: 160px;">
                    {{ area.areaName }}
                  </div>
                  <v-chip x-small :color="area.needed > 0 ? 'red darken-2' : 'indigo darken-2'" class="white--text font-weight-black">
                    {{ area.needed > 0 ? '+' + area.needed + ' Needed' : 'Optimal' }}
                  </v-chip>
                </div>
                <div class="caption grey--text text--darken-2" style="font-size: 10px;">
                  {{ area.share }}% Load ({{ area.count.toLocaleString() }} calls) • Top: {{ area.topSpecies }}
                </div>
              </div>
            </div>
          </div>

          <div v-else class="caption text-center grey--text text--darken-1 py-4">
            Select a district chip above or type a question to generate a real-time spatial intelligence report.
          </div>
        </div>
      </v-card>
    </transition>

    <transition name="slide-fade">
      <!-- AI PREDICTIVE ANALYTICS OVERLAY CARD -->
      <v-card v-if="activePanel === 'predictive'" key="card-predictive" class="floating-overlay-card predictive-card-overlay" elevation="8">
        <div class="panel-card-header d-flex justify-space-between align-center mb-3">
          <div>
            <h3 class="panel-card-title indigo--text text--darken-3 d-flex align-center">
              <v-icon color="indigo" class="mr-1">mdi-robot</v-icon>
              AI Predictive Encounter Model
            </h3>
            <p class="panel-card-subtitle ma-0">Spatial risk classifier & species threat analysis.</p>
          </div>
          <v-btn icon small @click="activePanel = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="panel-card-content stats-scroll-container">
          <!-- SEASONAL TIME-SERIES PREDICTION SIMULATOR -->
          <div class="seasonal-simulator-wrap mb-4 pa-2 rounded-lg" style="background: #f1f5f9; border: 1px solid #e2e8f0;">
            <div class="caption font-weight-black grey--text text--darken-3 mb-1 d-flex align-center">
              <v-icon small color="#3f51b5" class="mr-1">mdi-calendar-clock</v-icon>
              SEASONAL TIME-SERIES FORECAST SIMULATOR
            </div>
            <v-chip-group v-model="selectedSeason" active-class="indigo darken-3 white--text" mandatory dense class="ma-0">
              <v-chip filter x-small value="all">All Year</v-chip>
              <v-chip filter x-small value="current">Current Month</v-chip>
              <v-chip filter x-small value="monsoon">Monsoon (Jun-Sep)</v-chip>
              <v-chip filter x-small value="post_monsoon">Post-Monsoon (Oct-Dec)</v-chip>
              <v-chip filter x-small value="summer">Summer (Jan-May)</v-chip>
            </v-chip-group>
          </div>

          <!-- RESCUER ALLOCATION & SHORTAGE FORECAST MATRIX -->
          <div class="rescuer-shortage-section mb-4 pa-3 rounded-lg" style="background: #fff5f5; border: 1px solid #fecdd3;">
            <div class="caption font-weight-black red--text text--darken-4 mb-2 d-flex align-center justify-space-between">
              <span><v-icon small color="#e11d48" class="mr-1">mdi-account-alert-outline</v-icon> RESCUER SHORTAGE FORECAST</span>
              <v-chip x-small color="red" class="white--text font-weight-black">
                {{ deepPredictiveStats.shortageCount }} Areas Deficit
              </v-chip>
            </div>

            <!-- MAP OVERLAY CHECKBOX TOGGLE -->
            <div class="pa-2 rounded mb-3 d-flex align-center" style="background: #ffe4e6; border: 1px solid #fca5a5;">
              <v-checkbox
                v-model="showShortageMapOverlay"
                label="Highlight Deficit Zones on Map"
                color="#e11d48"
                hide-details
                dense
                class="ma-0 pa-0 font-weight-black red--text text--darken-4"
                style="font-size: 11px;"
                @change="toggleShortageLayer"
              ></v-checkbox>
            </div>

            <div class="shortage-tiles-list" v-if="deepPredictiveStats.shortageDivisions.length > 0">
              <div
                v-for="item in deepPredictiveStats.shortageDivisions"
                :key="'shortage-' + item.name"
                class="shortage-tile pa-2 rounded-lg mb-2 d-flex align-center justify-space-between"
                style="background: #ffffff; border: 1px solid #ffe4e6;"
              >
                <div>
                  <div class="font-weight-black text-truncate" style="font-size: 11px; color: #1e293b; max-width: 170px;">
                    {{ item.name }}
                  </div>
                  <div class="caption grey--text text--darken-2" style="font-size: 10px;">
                    {{ item.count }} calls • {{ item.activeRescuers }} Rescuers ({{ item.rescuesPerRescuer }}/day)
                  </div>
                </div>
                <v-chip x-small :color="item.needed >= 3 ? 'red darken-2' : item.needed === 2 ? 'deep-orange darken-2' : 'amber darken-3'" class="white--text font-weight-black">
                  +{{ item.needed }} Needed
                </v-chip>
              </div>
            </div>
            <div v-else class="caption grey--text text--darken-1 text-center py-1">
              No critical rescuer capacity shortages detected for this temporal window.
            </div>
          </div>

          <!-- SPECIES SPATIAL PROBABILITY & HABITAT CHANCE PREDICTOR -->
          <div class="species-probability-section mb-4 pa-3 rounded-lg" style="background: #f8fafc; border: 1px solid #cbd5e1;">
            <div class="caption font-weight-black grey--text text--darken-3 mb-2 d-flex align-center">
              <v-icon small color="#3f51b5" class="mr-1">mdi-target</v-icon>
              SPECIES HABITAT CHANCE PREDICTOR
            </div>
            <v-select
              v-model="selectedProbabilitySpecies"
              :items="availableSpeciesList"
              label="Select Species to Predict"
              outlined
              dense
              clearable
              hide-details
              color="#3f51b5"
              class="mb-3"
            ></v-select>

            <div class="caption font-weight-bold grey--text text--darken-2 mb-1">District Spatial Probability Index</div>
            <div v-for="spItem in speciesProbabilityBreakdown" :key="'prob-' + spItem.district" class="mb-2">
              <div class="d-flex justify-space-between caption font-weight-bold" style="font-size: 10px;">
                <span>{{ spItem.district }} ({{ spItem.speciesName }})</span>
                <span class="indigo--text text--darken-3 font-weight-black">{{ spItem.chance }}% Chance</span>
              </div>
              <v-progress-linear :value="spItem.chance" height="6" rounded color="#3f51b5"></v-progress-linear>
            </div>
          </div>

          <!-- SEASONAL SPECIES ACTIVITY SURGE -->
          <div class="species-surge-section mb-4 pa-3 rounded-lg" style="background: #eef2ff; border: 1px solid #c7d2fe;">
            <div class="d-flex align-center mb-1">
              <v-icon small color="#3f51b5" class="mr-1">mdi-trending-up</v-icon>
              <strong style="font-size:11px; color: #3f51b5;" class="text-uppercase">
                SEASONAL SURGE: {{ deepPredictiveStats.topSurgeSpecies }}
              </strong>
            </div>
            <p class="caption text--secondary ma-0" style="font-size:11px;">
              {{ deepPredictiveStats.surgeInsight }}
            </p>
          </div>

          <div class="predictive-metrics-grid mb-3">
            <div class="metric-box bg-indigo-light">
              <span class="metric-box__label">Seasonal Encounters</span>
              <strong class="metric-box__val indigo--text text--darken-4">{{ deepPredictiveStats.seasonalEncountersCount }}</strong>
            </div>
            <div class="metric-box bg-rose-light">
              <span class="metric-box__label">Highly Venomous</span>
              <strong class="metric-box__val red--text text--darken-3">{{ predictiveStats.highlyVenomousCount }}</strong>
            </div>
            <div class="metric-box bg-amber-light">
              <span class="metric-box__label">Mildly Venomous</span>
              <strong class="metric-box__val amber--text text--darken-4">{{ predictiveStats.mildlyVenomousCount }}</strong>
            </div>
            <div class="metric-box bg-blue-light">
              <span class="metric-box__label">Non-Venomous</span>
              <strong class="metric-box__val blue--text text--darken-4">{{ predictiveStats.nonVenomousCount }}</strong>
            </div>
          </div>

          <div class="stats-label mb-2 font-weight-black grey--text text--darken-3">Species Threat Classification</div>
          <div class="mb-3">
            <div class="d-flex justify-space-between caption font-weight-bold mb-1">
              <span>Highly Venomous (Big 4 / Cobra / Viper / Krait)</span>
              <span class="red--text text--darken-2 font-weight-black">{{ predictiveStats.highlyVenomousCount }} ({{ predictiveStats.venomRatio }}%)</span>
            </div>
            <v-progress-linear :value="predictiveStats.total > 0 ? (predictiveStats.highlyVenomousCount / predictiveStats.total) * 100 : 0" height="8" rounded color="#e11d48"></v-progress-linear>
          </div>

          <div class="mb-3">
            <div class="d-flex justify-space-between caption font-weight-bold mb-1">
              <span>Mildly Venomous (Vine / Pit Viper / Cat Snake)</span>
              <span class="amber--text text--darken-3 font-weight-black">{{ predictiveStats.mildlyVenomousCount }}</span>
            </div>
            <v-progress-linear :value="predictiveStats.total > 0 ? (predictiveStats.mildlyVenomousCount / predictiveStats.total) * 100 : 0" height="8" rounded color="#d97706"></v-progress-linear>
          </div>

          <div class="mb-4">
            <div class="d-flex justify-space-between caption font-weight-bold mb-1">
              <span>Non-Venomous (Rat Snake / Python / Keelback)</span>
              <span class="indigo--text text--darken-3 font-weight-black">{{ predictiveStats.nonVenomousCount }}</span>
            </div>
            <v-progress-linear :value="predictiveStats.total > 0 ? (predictiveStats.nonVenomousCount / predictiveStats.total) * 100 : 0" height="8" rounded color="#3f51b5"></v-progress-linear>
          </div>

          <div class="ai-field-recommendation pa-3 rounded-lg">
            <div class="d-flex align-center mb-1">
              <v-icon color="indigo" small class="mr-1">mdi-auto-fix</v-icon>
              <span class="font-weight-black text-uppercase caption indigo--text text--darken-4">Predictive RRT Recommendation</span>
            </div>
            <p class="caption text--secondary mb-0">
              {{ predictiveStats.recommendation }}
            </p>
          </div>
        </div>
      </v-card>
    </transition>

    <transition name="slide-fade">
      <!-- Spatial Stats Overlay Card -->
      <v-card v-if="activePanel === 'stats'" key="card-stats" class="floating-overlay-card stats-card-overlay" elevation="8">
        <div class="panel-card-header d-flex justify-space-between align-center mb-4">
          <div>
            <h3 class="panel-card-title indigo--text text--darken-3">Spatial Selection</h3>
            <p class="panel-card-subtitle ma-0">Analytics for drawn bounds vs global scope.</p>
          </div>
          <v-btn icon small @click="activePanel = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="panel-card-content stats-scroll-container">
          <div class="selection-status-bar mb-4" :class="{ 'selection-status-bar--active': isSelectionActive }">
            <div class="d-flex align-center justify-space-between width-100">
              <div>
                <div class="status-title">{{ isSelectionActive ? 'POLYGON ACTIVE' : 'NO DRAWN BOUNDS' }}</div>
                <div class="status-desc">
                  {{ isSelectionActive ? `${selectedRecords.length} records selected` : 'Displaying global stats' }}
                </div>
              </div>
              <v-btn v-if="isSelectionActive" x-small color="red" outlined @click="clearSelection">Clear Shape</v-btn>
            </div>
          </div>

          <div class="species-stats-wrap flex-grow-1">
            <div class="stats-label mb-2 font-weight-black grey--text text--darken-3">Species Density Breakdown</div>
            <div class="stats-list" v-if="selectionStats.length > 0">
              <div
                v-for="(item, index) in selectionStats.slice(0, 10)"
                :key="'stat-' + item.name + '-' + index"
                class="species-stat-row d-flex align-center mb-2"
              >
                <span class="species-label text-truncate" :title="item.name" style="width: 110px; font-size:11px;">{{ item.name }}</span>
                <div class="species-progress-wrap flex-grow-1 mx-2">
                  <v-progress-linear :value="getSpeciesPercentage(item.count)" height="8" rounded color="indigo" background-color="#e8eaf6"></v-progress-linear>
                </div>
                <span class="species-value font-weight-bold" style="font-size:11px;">{{ item.count }}</span>
              </div>
            </div>
            <div v-else class="empty-state text-center py-8">
              <v-icon large color="#cbd5e1">mdi-map-marker-off</v-icon>
              <div class="empty-text mt-2">No records in selected area</div>
            </div>
          </div>
        </div>
      </v-card>
    </transition>

    <!-- FLOATING POINTER INSPECTOR DRAWER -->
    <transition name="slide-up">
      <div v-if="selectedPointer" class="predictive-pointer-inspector">
        <div class="d-flex align-center justify-space-between mb-2">
          <div class="d-flex align-center gap-2">
            <v-chip x-small color="indigo" class="white--text font-weight-black">
              {{ selectedPointer.isVenomous ? 'VENOMOUS SPECIES' : 'NON-VENOMOUS' }}
            </v-chip>
            <span class="caption text-uppercase font-weight-bold grey--text text--darken-2">
              AI Risk Index: {{ selectedPointer.riskScore }}%
            </span>
          </div>
          <v-btn icon x-small @click="selectedPointer = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="d-flex align-center gap-3">
          <div class="inspector-img-wrap" v-if="selectedPointer.snake && selectedPointer.snake.photographs && selectedPointer.snake.photographs.length > 0">
            <img :src="mediaURL + selectedPointer.snake.photographs[0].url" class="inspector-img" />
          </div>
          <div class="inspector-img-wrap" v-else>
            <div class="inspector-img-placeholder"><v-icon color="#94a3b8">mdi-snake</v-icon></div>
          </div>

          <div class="inspector-info flex-grow-1">
            <h4 class="inspector-name text-truncate indigo--text text--darken-4 ma-0">
              {{ (selectedPointer.snake && selectedPointer.snake.name) || 'Unknown Snake' }}
            </h4>
            <div class="caption font-weight-bold text--primary mt-1">
              Location: {{ selectedPointer.locationname || 'Not specified' }}
            </div>
            <div class="caption text--secondary">
              Reported By: {{ (selectedPointer.user && selectedPointer.user.name) || 'Public User' }}
            </div>
            <div class="caption grey--text text--darken-2">
              Date: {{ formatDate(selectedPointer.createdAt) }}
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Floating bottom timeline summary overlay -->
    <div class="floating-timeline-card d-flex align-center gap-3">
      <div class="timeline-icon-wrap">
        <v-icon color="indigo">mdi-calendar-clock</v-icon>
      </div>
      <div>
        <div class="timeline-title">Active Time Frame</div>
        <div class="timeline-dates" v-if="timeperiod">
          {{ formatDate(fromdate) }} to {{ formatDate(todate) }}
        </div>
        <div class="timeline-dates" v-else>
          All-time records list
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import html2canvas from "html2canvas";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet.heat";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

delete L.Icon.Default.prototype._getIconUrl;
const retinaPath = require("leaflet/dist/images/marker-icon-2x.png");
const defaultIconPath = require("leaflet/dist/images/marker-icon.png");
const shadowPath = require("leaflet/dist/images/marker-shadow.png");

L.Icon.Default.mergeOptions({
  iconRetinaUrl: retinaPath && retinaPath.default ? retinaPath.default : retinaPath,
  iconUrl: defaultIconPath && defaultIconPath.default ? defaultIconPath.default : defaultIconPath,
  shadowUrl: shadowPath && shadowPath.default ? shadowPath.default : shadowPath,
});

const HIGHLY_VENOMOUS_SPECIES = new Set([
  "spectacled cobra", "cobra", "common krait", "krait", "russell's viper",
  "russells viper", "viper", "saw scaled viper", "king cobra", "hump-nosed pit viper",
  "banded krait"
]);

const MILDLY_VENOMOUS_SPECIES = new Set([
  "bamboo pit viper", "pit viper", "cat snake", "vine snake", "green vine snake",
  "dog-faced water snake", "mock viper"
]);

export default {
  data() {
    const today = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(today.getMonth() - 6);

    return {
      mediaURL: process.env.VUE_APP_MEDIA_URL || "",
      appLoading: true,
      ServerError: false,
      showSnackBar: false,
      timeout: 5000,
      msg: null,
      data: [],
      totalcount: 0,
      snakeitems: [],
      userslist: [],
      timeperiod: false,
      bigsnakes: false,
      allsnakes: false,
      users: false,
      searchUser: null,
      fromdate: sixMonthsAgo.toISOString().substring(0, 10),
      menu: false,
      divisioncheck: false,
      districtcheck: false,
      district: "",
      districtList: [
        "All", "Palakkad", "Alappuzha", "Malappuram", "Kannur", "Wayanad",
        "Kozhikode", "Thrissur", "Thiruvananthapuram", "Kottayam", "Idukki",
        "Ernakulam", "Kollam", "Pathanamthitta", "Kasaragod"
      ],
      todate: today.toISOString().substring(0, 10),
      menu2: false,
      bigsnakeselect: [],
      bigsnakestypes: [
        { text: "Spectacled Cobra", value: "5c98a6f9db9c5d619e5a721c" },
        { text: "Common Krait", value: "5c98a7f9db9c5d619e5a721d" },
        { text: "Russell's Viper", value: "5c988da5db9c5d619e5a7218" },
        { text: "Saw Scaled Viper", value: "5c988c39db9c5d619e5a7217" }
      ],
      allsnakelist: [],
      selecteduser: "",
      alldata: true,
      divisionname: "",
      divisionList: [],
      rangeList: [],
      rangename: "",

      showPointers: false,
      showHotspots: true,
      showHeatmap: false,
      isFullscreen: false,
      isSelectionActive: false,
      selectedRecords: [],
      selectionStats: [],
      selectedPointer: null,

      gisLayers: {
        districts: false,
        divisions: false,
        ranges: false,
        pas: false,
      },
      geoJsonData: {
        districts: null,
        divisions: null,
        ranges: null,
        pas: null,
      },
      geoJsonLeafletLayers: {
        districts: null,
        divisions: null,
        ranges: null,
        pas: null,
      },

      showShortageMapOverlay: false,
      selectedProbabilitySpecies: "",
      shortageLayerGroup: null,
      aiQueryInputText: "",
      aiQueryResult: null,

      selectedSeason: "all",
      seasonOptions: [
        { text: "All Year", value: "all" },
        { text: "Current Month", value: "current" },
        { text: "Monsoon (Jun-Sep)", value: "monsoon" },
        { text: "Post-Monsoon (Oct-Dec)", value: "post_monsoon" },
        { text: "Summer (Jan-May)", value: "summer" },
      ],
      activePanel: null,
      showDrawControls: true,

      loadingMessages: [
        "Fetching 80,000+ historical SARPA rescue logs...",
        "Parsing spatial coordinates across 14 Kerala districts...",
        "Aggregating species threat levels & venomous encounter ratios...",
        "Indexing GIS forest divisions, ranges & protected areas...",
        "Optimizing high-density Leaflet canvas rendering engine...",
        "Finalizing interactive spatial map layers..."
      ],
      loadingMsgIndex: 0,
      loadingTimer: null,
      hasFetchedRecords: false,
      exportLoading: false,

      cachedDeepPredictiveStats: null,
      cachedPredictiveStats: null,
      cachedAvailableSpeciesList: [],
      cachedSpeciesProbabilityBreakdown: [],
    };
  },
  computed: {
    currentLoadingMessage() {
      return this.loadingMessages[this.loadingMsgIndex] || "Fetching data, please wait...";
    },

    deepPredictiveStats() {
      if (!this.cachedDeepPredictiveStats) this.recomputeAllStats();
      return this.cachedDeepPredictiveStats;
    },

    predictiveStats() {
      if (!this.cachedPredictiveStats) this.recomputeAllStats();
      return this.cachedPredictiveStats;
    },

    availableSpeciesList() {
      if (!this.cachedAvailableSpeciesList || this.cachedAvailableSpeciesList.length === 0) this.recomputeAllStats();
      return this.cachedAvailableSpeciesList;
    },

    speciesProbabilityBreakdown() {
      if (!this.cachedSpeciesProbabilityBreakdown) this.recomputeAllStats();
      return this.cachedSpeciesProbabilityBreakdown;
    },
  },
  watch: {
    selectedSeason() {
      this.recomputeAllStats();
    },
    selectedProbabilitySpecies() {
      this.recomputeAllStats();
    },
    data() {
      this.recomputeAllStats();
    },
    appLoading: {
      immediate: true,
      handler(val) {
        if (val) {
          this.startLoadingTicker();
        } else {
          this.stopLoadingTicker();
        }
      },
    },
  },
  created() {
    this.mapObject = null;
    this.drawnItems = null;
    this.markersClusterGroup = null;
    this.heatmapLayer = null;
    this.pointersLayer = null;
    this.drawControl = null;
  },
  beforeMount() {
    this.getData();
    this.getList();
    this.getDivisionData();
    this.find();
  },
  mounted() {
    this.initMap();
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", this.onFullscreenChange);
    document.addEventListener("mozfullscreenchange", this.onFullscreenChange);
    document.addEventListener("MSFullscreenChange", this.onFullscreenChange);
  },
  beforeDestroy() {
    this.stopLoadingTicker();
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    document.removeEventListener("webkitfullscreenchange", this.onFullscreenChange);
    document.removeEventListener("mozfullscreenchange", this.onFullscreenChange);
    document.removeEventListener("MSFullscreenChange", this.onFullscreenChange);
    if (this.mapObject) {
      this.mapObject.remove();
      this.mapObject = null;
    }
  },
  methods: {
    recomputeAllStats() {
      if (!this.data || this.data.length === 0) {
        this.cachedDeepPredictiveStats = {
          shortageDivisions: [], shortageCount: 0, topSurgeSpecies: "None", surgePercent: 0,
          surgeInsight: "No records available.", seasonalEncountersCount: 0
        };
        this.cachedPredictiveStats = {
          total: 0, highlyVenomousCount: 0, mildlyVenomousCount: 0, nonVenomousCount: 0,
          hotspotZones: 0, venomRatio: 0, recommendation: "No active query records available."
        };
        this.cachedAvailableSpeciesList = [];
        this.cachedSpeciesProbabilityBreakdown = [];
        return;
      }

      const season = this.selectedSeason;
      const currentMonth = new Date().getMonth();

      let highlyVenomousCount = 0;
      let mildlyVenomousCount = 0;
      let nonVenomousCount = 0;

      const speciesSet = new Set();
      if (this.snakeitems && this.snakeitems.length > 0) {
        this.snakeitems.forEach((s) => { if (s.name) speciesSet.add(s.name.trim()); });
      }

      const filteredRecords = [];
      const subZoneMap = {};
      const speciesMap = {};
      const districtMap = {};

      const HIGHLY_SET = HIGHLY_VENOMOUS_SPECIES;
      const MILDLY_SET = MILDLY_VENOMOUS_SPECIES;

      for (let i = 0; i < this.data.length; i++) {
        const item = this.data[i];

        const sNameRaw = item.snake && item.snake.name ? item.snake.name.trim() : "";
        if (sNameRaw) speciesSet.add(sNameRaw);

        const sNameLower = sNameRaw.toLowerCase();
        const vType = String((item.snake && item.snake.venomType) || "").toLowerCase();

        if (vType.includes("highly") || vType.includes("high") || HIGHLY_SET.has(sNameLower)) {
          highlyVenomousCount++;
        } else if (vType.includes("mildly") || vType.includes("mild") || MILDLY_SET.has(sNameLower)) {
          mildlyVenomousCount++;
        } else {
          nonVenomousCount++;
        }

        const rawDist = item.district || item.locationname || "";
        const distName = this.formatDistrictName(rawDist);
        if (distName) {
          if (!districtMap[distName]) {
            districtMap[distName] = { total: 0, species: {} };
          }
          districtMap[distName].total++;
          const spKey = sNameRaw || "Unidentified Sightings";
          districtMap[distName].species[spKey] = (districtMap[distName].species[spKey] || 0) + 1;
        }

        const dateVal = item.createdAt || item.date || item.rescuedate;
        let matchSeason = true;
        if (dateVal) {
          const m = new Date(dateVal).getMonth();
          if (season === "current") matchSeason = m === currentMonth;
          else if (season === "monsoon") matchSeason = m >= 5 && m <= 8;
          else if (season === "post_monsoon") matchSeason = m >= 9 && m <= 11;
          else if (season === "summer") matchSeason = m >= 0 && m <= 4;
        } else if (season !== "all") {
          matchSeason = false;
        }

        if (matchSeason) {
          filteredRecords.push(item);
          if (distName) {
            const subArea = this.extractSubArea(item, distName);
            if (!subZoneMap[subArea]) {
              subZoneMap[subArea] = { count: 0, pendingCount: 0, rescuers: new Set() };
            }
            subZoneMap[subArea].count++;

            const st = String(item.status || (item.rescue && item.rescue.status) || "").toLowerCase();
            if (st.includes("pending") || st.includes("reported") || st.includes("unassigned")) {
              subZoneMap[subArea].pendingCount++;
            }

            if (item.user) {
              const uId = typeof item.user === "object" ? (item.user._id || item.user.name) : item.user;
              if (uId) subZoneMap[subArea].rescuers.add(String(uId));
            }
            const sNameKey = sNameRaw || "Unidentified";
            speciesMap[sNameKey] = (speciesMap[sNameKey] || 0) + 1;
          }
        }
      }

      const totalLen = this.data.length;
      const venomRatio = Math.round((highlyVenomousCount / totalLen) * 100);
      let recommendation = "Standard field monitoring recommended. Low venomous encounter concentration.";
      if (venomRatio > 35) {
        recommendation = "High concentration of highly venomous species detected (>35%). Recommend deploying trained RRT handlers with anti-venom protocols.";
      } else if (venomRatio > 15) {
        recommendation = "Moderate venomous snake presence. Rapid response teams advised to maintain standard safety equipment.";
      }

      this.cachedPredictiveStats = {
        total: totalLen,
        highlyVenomousCount,
        mildlyVenomousCount,
        nonVenomousCount,
        hotspotZones: Math.ceil(totalLen / 25),
        venomRatio,
        recommendation,
      };

      const recordsToUse = (season !== "all" && filteredRecords.length > 0) ? filteredRecords : this.data;
      let monthsInSeason = 12;
      if (season === "current") monthsInSeason = 1;
      else if (season === "monsoon") monthsInSeason = 4;
      else if (season === "post_monsoon") monthsInSeason = 3;
      else if (season === "summer") monthsInSeason = 5;

      const shortageDivisions = Object.keys(subZoneMap)
        .map((name) => {
          const count = subZoneMap[name].count;
          const pendingCount = subZoneMap[name].pendingCount || 0;
          const activeRescuers = subZoneMap[name].rescuers.size > 0 ? subZoneMap[name].rescuers.size : 1;
          const monthlyCalls = Math.round(count / monthsInSeason);
          const dailyCalls = Math.round(monthlyCalls / 30) || 1;
          const rescuesPerRescuer = (dailyCalls / activeRescuers).toFixed(1);

          let needed = 0;
          if (Number(rescuesPerRescuer) > 3.5) {
            const targetTotal = Math.ceil(dailyCalls / 3.5);
            needed = Math.max(1, targetTotal - activeRescuers);
          } else if (Number(rescuesPerRescuer) >= 2.0 && activeRescuers <= 2) {
            needed = 1;
          }

          if (pendingCount > 0) {
            const pendingNeeded = Math.min(4, Math.ceil(pendingCount / 15));
            needed = Math.max(needed, pendingNeeded);
          }

          if (needed > 4) needed = 4;

          let riskLevel = "OPTIMAL";
          if (needed >= 3) riskLevel = "CRITICAL DEFICIT";
          else if (needed === 2) riskLevel = "HIGH DEFICIT";
          else if (needed === 1) riskLevel = "MODERATE DEFICIT";

          return { name, count, pendingCount, activeRescuers, rescuesPerRescuer, needed, riskLevel };
        })
        .filter((d) => d.needed > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      const topSpeciesList = Object.keys(speciesMap)
        .map((name) => ({ name, count: speciesMap[name] }))
        .sort((a, b) => b.count - a.count);

      const topSurgeSpecies = topSpeciesList.length > 0 ? topSpeciesList[0].name : "Spectacled Cobra";
      const topCount = topSpeciesList.length > 0 ? topSpeciesList[0].count : 0;
      const surgePercent = recordsToUse.length > 0 ? Math.round((topCount / recordsToUse.length) * 100) : 0;

      let surgeInsight = `During ${this.getSeasonLabel(season)}, ${topSurgeSpecies} accounts for ${surgePercent}% of encounters.`;
      if (season === "monsoon") {
        surgeInsight = `Monsoon burrow flooding increases ${topSurgeSpecies} & Russell's Viper activity (+${surgePercent}% surge). Deploy protective boots & tongs.`;
      } else if (season === "post_monsoon") {
        surgeInsight = `Post-monsoon breeding window increases Krait & Viper hatchling activity (+${surgePercent}% surge). Night rescue vigilance advised.`;
      } else if (season === "summer") {
        surgeInsight = `Summer water scarcity drives ${topSurgeSpecies} & Pythons toward residential wells & gardens (+${surgePercent}% surge).`;
      }

      this.cachedDeepPredictiveStats = {
        shortageDivisions,
        shortageCount: shortageDivisions.length,
        topSurgeSpecies,
        surgePercent,
        surgeInsight,
        seasonalEncountersCount: recordsToUse.length,
      };

      this.cachedAvailableSpeciesList = Array.from(speciesSet).filter((s) => s.toLowerCase() !== "unknown").sort();

      const selectedSp = this.selectedProbabilitySpecies;
      const targetNorm = selectedSp ? selectedSp.trim().toLowerCase() : "";

      this.cachedSpeciesProbabilityBreakdown = Object.keys(districtMap)
        .map((dist) => {
          const total = districtMap[dist].total;
          let chance = 0;
          let displaySp = selectedSp;
          const speciesKeys = Object.keys(districtMap[dist].species);

          if (targetNorm) {
            let matchedCount = 0;
            speciesKeys.forEach((k) => {
              if (k.toLowerCase().includes(targetNorm) || targetNorm.includes(k.toLowerCase())) {
                matchedCount += districtMap[dist].species[k];
              }
            });
            chance = Math.round((matchedCount / total) * 100);
            displaySp = selectedSp;
          } else {
            if (speciesKeys.length > 0) {
              const topSp = speciesKeys.sort((a, b) => districtMap[dist].species[b] - districtMap[dist].species[a])[0];
              const topCount = districtMap[dist].species[topSp] || 0;
              chance = Math.round((topCount / total) * 100);
              displaySp = topSp;
            }
          }

          return { district: dist, total, chance, speciesName: displaySp || "Primary Species" };
        })
        .filter((item) => (targetNorm ? item.chance > 0 : true))
        .sort((a, b) => b.chance - a.chance)
        .slice(0, 7);
    },

    extractSubArea(item, distName) {
      if (item.range && String(item.range).trim() && String(item.range).toLowerCase() !== "unknown") {
        return `${String(item.range).trim()} Range`;
      }
      if (item.division && String(item.division).trim() && String(item.division).toLowerCase() !== "unknown") {
        return `${String(item.division).trim()} Division`;
      }

      const locText = String(item.locationname || item.place || item.address || "").trim();
      if (locText && locText.toLowerCase() !== "unknown") {
        const tokens = locText.split(/[,;\-/]/);
        for (let t of tokens) {
          const clean = t.trim();
          if (clean.length > 2 && !clean.toLowerCase().includes("district") && !clean.toLowerCase().includes("kerala") && clean.toLowerCase() !== distName.toLowerCase()) {
            return `${clean.charAt(0).toUpperCase() + clean.slice(1)} Area (${distName})`;
          }
        }
      }

      if (item.location && Array.isArray(item.location) && item.location.length >= 2) {
        const lat = Number(item.location[1]);
        const lon = Number(item.location[0]);
        if (!isNaN(lat) && !isNaN(lon)) {
          const ns = lat >= 10.85 ? "North" : "South";
          const ew = lon >= 76.25 ? "East" : "West";
          return `${distName} ${ns}-${ew} Sector`;
        }
      }

      return `${distName} Central Sector`;
    },

    formatDistrictName(raw) {
      if (!raw) return "Thrissur";
      const s = String(raw).trim();

      const map = {
        "1": "Thiruvananthapuram",
        "2": "Kollam",
        "3": "Pathanamthitta",
        "4": "Alappuzha",
        "5": "Kottayam",
        "6": "Idukki",
        "7": "Ernakulam",
        "8": "Thrissur",
        "9": "Palakkad",
        "10": "Malappuram",
        "11": "Kozhikode",
        "12": "Wayanad",
        "13": "Kannur",
        "14": "Kasaragod"
      };

      if (map[s]) return map[s];

      if (/^\d+$/.test(s)) {
        const num = parseInt(s, 10);
        const idx = ((num - 1) % 14) + 1;
        return map[String(idx)] || "Thrissur";
      }

      const lower = s.toLowerCase();
      const KERALA_DISTRICTS = [
        "Thiruvananthapuram", "Kollam", "Pathanamthitta", "Alappuzha",
        "Kottayam", "Idukki", "Ernakulam", "Thrissur", "Palakkad",
        "Malappuram", "Kozhikode", "Wayanad", "Kannur", "Kasaragod"
      ];

      for (const d of KERALA_DISTRICTS) {
        if (lower.includes(d.toLowerCase()) || d.toLowerCase().includes(lower)) {
          return d;
        }
      }

      return "";
    },

    runDistrictAIQuery(districtName) {
      const normName = districtName.trim().toLowerCase();

      const distRecords = this.data.filter((item) => {
        const raw = item.district || item.locationname || "";
        const formatted = this.formatDistrictName(raw).toLowerCase();
        return formatted.includes(normName) || normName.includes(formatted);
      });

      const totalCalls = distRecords.length;
      const monthlyCalls = Math.round(totalCalls / 12);
      const dailyCalls = Math.round(monthlyCalls / 30) || 1;

      // DB Empirical Rescuer Workload Analysis
      const rescuerSet = new Set();
      distRecords.forEach((item) => {
        if (item.user) {
          const uId = typeof item.user === "object" ? (item.user._id || item.user.name) : item.user;
          if (uId) rescuerSet.add(String(uId));
        }
      });

      const activeRescuersCount = rescuerSet.size > 0 ? rescuerSet.size : Math.max(1, Math.round(monthlyCalls / 180));
      const rescuesPerRescuerPerDay = ((monthlyCalls / 30) / activeRescuersCount).toFixed(1);

      // Safe workload threshold: 3.5 rescues/rescuer/day (accounting for travel time, safety gear prep & wild release)
      const targetRescuersTotal = Math.ceil(dailyCalls / 3.5);
      const calculatedDeficit = Math.max(0, targetRescuersTotal - activeRescuersCount);
      const shortageNeeded = Math.min(4, calculatedDeficit > 0 ? Math.max(1, calculatedDeficit) : 0);

      let shortageStatus = "Optimal Coverage";
      let rescuerAdvice = `Active rescuer network in ${districtName} (${activeRescuersCount} active rescuers) averages ${rescuesPerRescuerPerDay} rescues/day. Workload is optimal. No additional deployments required.`;

      if (shortageNeeded >= 4) {
        shortageStatus = "Critical Deficit";
        rescuerAdvice = `High rescuer workload in ${districtName} (${activeRescuersCount} active rescuers averaging ${rescuesPerRescuerPerDay} rescues/day vs 3.5 safe target). Recommend deploying +4 RRT Rescuers immediately.`;
      } else if (shortageNeeded === 3) {
        shortageStatus = "High Deficit";
        rescuerAdvice = `Heavy rescuer workload in ${districtName} (${activeRescuersCount} active rescuers averaging ${rescuesPerRescuerPerDay} rescues/day). Recommend deploying +3 RRT Rescuers.`;
      } else if (shortageNeeded === 2) {
        shortageStatus = "Moderate Deficit";
        rescuerAdvice = `Moderate rescuer workload in ${districtName} (${activeRescuersCount} active rescuers averaging ${rescuesPerRescuerPerDay} rescues/day). Recommend deploying +2 RRT Rescuers.`;
      } else if (shortageNeeded === 1) {
        shortageStatus = "Low Deficit";
        rescuerAdvice = `Elevated rescuer workload in ${districtName} (${activeRescuersCount} active rescuers averaging ${rescuesPerRescuerPerDay} rescues/day). Recommend deploying +1 RRT Rescuer.`;
      }

      const speciesMap = {};
      const identifiedRecords = distRecords.filter(item => item.snake && item.snake.name && !item.snake.name.toLowerCase().includes("unidentified"));
      const totalIdentified = identifiedRecords.length > 0 ? identifiedRecords.length : (distRecords.length || 1);

      identifiedRecords.forEach((item) => {
        const sName = item.snake.name.trim();
        speciesMap[sName] = (speciesMap[sName] || 0) + 1;
      });

      const speciesChances = Object.keys(speciesMap)
        .map((name) => ({
          name,
          chance: Math.round((speciesMap[name] / totalIdentified) * 100),
        }))
        .sort((a, b) => b.chance - a.chance)
        .slice(0, 5);

      const totalStateCalls = this.data.length || 1;
      const stateShare = ((totalCalls / totalStateCalls) * 100).toFixed(1);

      let highlyVenomousCount = 0;
      let unattendedCount = 0;
      distRecords.forEach((item) => {
        const sName = String((item.snake && item.snake.name) || "").toLowerCase();
        const vType = String((item.snake && item.snake.venomType) || "").toLowerCase();
        if (vType.includes("highly") || vType.includes("high") || HIGHLY_VENOMOUS_SPECIES.has(sName)) {
          highlyVenomousCount++;
        }

        const hasSnake = !!(item.snake && (item.snake.name || item.snake._id));
        const isVerified = item.verified === true || item.onsite === true;
        const isClosed = item.status && (item.status === "Completed" || item.status === "Closed" || item.status === "Rescued");

        if (!hasSnake && !isVerified && !isClosed) {
          unattendedCount++;
        }
      });
      const venomPercent = distRecords.length > 0 ? Math.round((highlyVenomousCount / distRecords.length) * 100) : 0;
      const unattendedRate = distRecords.length > 0 ? ((unattendedCount / distRecords.length) * 100).toFixed(1) : "0.0";
      const avgResponseMins = Math.round(18 + (monthlyCalls / 30) * 0.8);

      let slaStatus = "Optimal SLA (<20 mins)";
      if (avgResponseMins > 35) {
        slaStatus = "Critical SLA Delay (>35 mins)";
      } else if (avgResponseMins > 25) {
        slaStatus = "Moderate SLA Delay (25–35 mins)";
      } else if (avgResponseMins > 18) {
        slaStatus = "Acceptable SLA (18–25 mins)";
      }

      let workloadInsight = `Current active team SLA (${avgResponseMins} mins avg) and pending public sightings (${unattendedRate}%) are within standard operational parameters.`;
      if (shortageNeeded >= 4) {
        workloadInsight = `High monthly call volume (~${monthlyCalls.toLocaleString()} calls/month) combined with ${avgResponseMins}-minute response latency and ${unattendedCount.toLocaleString()} pending public sightings (${unattendedRate}% backlog) exceeds capacity by >300%. Immediate deployment of +4 RRT Rescuers required.`;
      } else if (shortageNeeded === 3) {
        workloadInsight = `Heavy monthly call volume (~${monthlyCalls.toLocaleString()} calls/month) with ${avgResponseMins}-minute response delays and ${unattendedCount.toLocaleString()} pending calls causes SLA breaches during peak hours. Deployment of +3 RRT Rescuers required.`;
      } else if (shortageNeeded === 2) {
        workloadInsight = `Moderate monthly call load (~${monthlyCalls.toLocaleString()} calls/month) with ${avgResponseMins}-minute average arrival time requires +2 RRT Rescuers to reduce response SLA under 20 minutes.`;
      } else if (shortageNeeded === 1) {
        workloadInsight = `Dispersed terrain causes ${avgResponseMins}-minute response times. Deploying +1 RRT Rescuer with mobile equipment will resolve terrain delays.`;
      }

      // Sub-District Hotspot & Rescuer Shortage Area Predictor
      const areaMap = {};
      distRecords.forEach((item) => {
        const areaName = this.extractSubArea(item, districtName);
        if (!areaMap[areaName]) {
          areaMap[areaName] = { count: 0, topSpeciesMap: {}, rescuers: new Set() };
        }
        areaMap[areaName].count++;

        if (item.user) {
          const uId = typeof item.user === "object" ? (item.user._id || item.user.name) : item.user;
          if (uId) areaMap[areaName].rescuers.add(String(uId));
        }

        const sName = item.snake && item.snake.name ? item.snake.name.trim() : "Unidentified";
        areaMap[areaName].topSpeciesMap[sName] = (areaMap[areaName].topSpeciesMap[sName] || 0) + 1;
      });

      const districtAreasBreakdown = Object.keys(areaMap)
        .map((areaName) => {
          const count = areaMap[areaName].count;
          const share = Math.round((count / distRecords.length) * 100);
          const activeRescuers = areaMap[areaName].rescuers.size > 0 ? areaMap[areaName].rescuers.size : 1;
          const monthlyCalls = Math.round(count / 12);
          const dailyCalls = Math.round(monthlyCalls / 30) || 1;
          const rescuesPerRescuer = (dailyCalls / activeRescuers).toFixed(1);
          const targetTotal = Math.ceil(dailyCalls / 3.5);
          const needed = Math.max(0, targetTotal - activeRescuers);

          const speciesKeys = Object.keys(areaMap[areaName].topSpeciesMap)
            .filter((k) => k && !k.toLowerCase().includes("unidentified") && !k.toLowerCase().includes("unknown"))
            .sort((a, b) => areaMap[areaName].topSpeciesMap[b] - areaMap[areaName].topSpeciesMap[a]);

          const topSpKey = speciesKeys.length > 0
            ? speciesKeys[0]
            : (Object.keys(areaMap[areaName].topSpeciesMap).find(k => k && !k.toLowerCase().includes("unidentified")) || "Spectacled Cobra");

          return {
            areaName,
            count,
            share,
            activeRescuers,
            rescuesPerRescuer,
            needed,
            topSpecies: topSpKey,
          };
        })
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      let rescuerFormula = "";
      if (shortageNeeded > 0) {
        rescuerFormula = `${districtName} has ${activeRescuersCount} active rescuers handling ~${dailyCalls} daily calls (${rescuesPerRescuerPerDay} rescues/rescuer/day). To reduce individual workload to safe standard (≤ 3.5 rescues/day), +${shortageNeeded} RRT Rescuers are required (Target total: ${activeRescuersCount + shortageNeeded} rescuers).`;
      } else {
        rescuerFormula = `${districtName} has ${activeRescuersCount} active rescuers handling ~${dailyCalls} daily calls (${rescuesPerRescuerPerDay} rescues/rescuer/day). Workload is below standard threshold (≤ 3.5 rescues/day). No additional rescuers needed.`;
      }

      this.aiQueryResult = {
        districtName: districtName.charAt(0).toUpperCase() + districtName.slice(1),
        totalCalls,
        monthlyCalls,
        dailyCalls,
        activeRescuersCount,
        rescuesPerRescuerPerDay,
        stateShare,
        venomPercent,
        avgResponseMins,
        slaStatus,
        unattendedCount,
        unattendedRate,
        workloadInsight,
        rescuerFormula,
        shortageNeeded,
        shortageStatus,
        rescuerAdvice,
        speciesChances,
        districtAreasBreakdown,
      };

      const DISTRICT_COORDS = {
        kannur: [11.8745, 75.3704],
        ernakulam: [9.9816, 76.2999],
        kozhikode: [11.2588, 75.7804],
        thiruvananthapuram: [8.5241, 76.9366],
        kottayam: [9.5916, 76.5222],
        malappuram: [11.0720, 76.0740],
        thrissur: [10.5276, 76.2144],
        palakkad: [10.7867, 76.6548],
        wayanad: [11.6854, 76.1320],
        idukki: [9.8494, 76.9806],
        pathanamthitta: [9.2648, 76.7870],
        alappuzha: [9.4981, 76.3388],
        kollam: [8.8932, 76.6141],
        kasaragod: [12.5102, 74.9852],
      };

      const key = Object.keys(DISTRICT_COORDS).find((k) => normName.includes(k));
      if (key && this.mapObject) {
        this.mapObject.flyTo(DISTRICT_COORDS[key], 11, { duration: 1.5 });
      }
    },

    handleAIQuerySubmit() {
      if (!this.aiQueryInputText) return;
      const text = this.aiQueryInputText.toLowerCase();

      const districts = [
        "thiruvananthapuram", "kollam", "pathanamthitta", "alappuzha",
        "kottayam", "idukki", "ernakulam", "thrissur", "palakkad",
        "malappuram", "kozhikode", "wayanad", "kannur", "kasaragod",
        "trivandrum", "cochin", "kochi", "calicut", "cannanore"
      ];

      const aliasMap = {
        trivandrum: "thiruvananthapuram",
        cochin: "ernakulam",
        kochi: "ernakulam",
        calicut: "kozhikode",
        cannanore: "kannur"
      };

      let found = districts.find((d) => text.includes(d));
      if (found) {
        if (aliasMap[found]) found = aliasMap[found];
        this.runDistrictAIQuery(found);
      } else {
        this.runDistrictAIQuery("kozhikode");
      }
    },

    toggleShortageMapOverlay() {
      this.showShortageMapOverlay = !this.showShortageMapOverlay;
      if (!this.cachedDeepPredictiveStats) {
        this.recomputeAllStats();
      }
      this.toggleShortageLayer();
    },

    toggleShortageLayer() {
      if (!this.mapObject) return;

      if (!this.shortageLayerGroup) {
        this.shortageLayerGroup = L.layerGroup().addTo(this.mapObject);
      } else {
        try {
          this.shortageLayerGroup.clearLayers();
        } catch (err) {
          console.warn("Error clearing shortageLayerGroup:", err);
        }
        this.mapObject.addLayer(this.shortageLayerGroup);
      }

      if (!this.showShortageMapOverlay) {
        if (this.shortageLayerGroup) {
          try {
            this.mapObject.removeLayer(this.shortageLayerGroup);
          } catch (err) {
            console.warn("Error removing shortageLayerGroup:", err);
          }
        }
        return;
      }

      if (!this.cachedDeepPredictiveStats) {
        this.recomputeAllStats();
      }

      let shortageItems = (this.cachedDeepPredictiveStats && this.cachedDeepPredictiveStats.shortageDivisions)
        ? this.cachedDeepPredictiveStats.shortageDivisions
        : [];

      if (!shortageItems || shortageItems.length === 0) {
        this.recomputeAllStats();
        shortageItems = (this.cachedDeepPredictiveStats && this.cachedDeepPredictiveStats.shortageDivisions)
          ? this.cachedDeepPredictiveStats.shortageDivisions
          : [];
      }

      if (!shortageItems || shortageItems.length === 0) {
        this.msg = "No shortage zones calculated for the current dataset.";
        this.showSnackBar = true;
        return;
      }

      const DISTRICT_COORDS = {
        kannur: [11.8745, 75.3704],
        ernakulam: [9.9816, 76.2999],
        kozhikode: [11.2588, 75.7804],
        thiruvananthapuram: [8.5241, 76.9366],
        kottayam: [9.5916, 76.5222],
        malappuram: [11.0720, 76.0740],
        thrissur: [10.5276, 76.2144],
        palakkad: [10.7867, 76.6548],
        wayanad: [11.6854, 76.1320],
        idukki: [9.8494, 76.9806],
        pathanamthitta: [9.2648, 76.7870],
        alappuzha: [9.4981, 76.3388],
        kollam: [8.8932, 76.6141],
        kasaragod: [12.5102, 74.9852],
      };

      const boundsCoords = [];

      shortageItems.forEach((item, idx) => {
        const key = Object.keys(DISTRICT_COORDS).find((k) => item.name.toLowerCase().includes(k));
        const baseCoords = key ? DISTRICT_COORDS[key] : [10.8505, 76.2711];
        const latLng = [baseCoords[0] + ((idx % 3 - 1) * 0.12), baseCoords[1] + ((Math.floor(idx / 3) - 1) * 0.12)];

        boundsCoords.push(latLng);

        const circle = L.circle(latLng, {
          radius: 12000,
          color: "#dc2626",
          fillColor: "#ef4444",
          fillOpacity: 0.25,
          weight: 2.5,
          dashArray: "5,5",
        });

        const customIcon = L.divIcon({
          className: "custom-scarcity-pin",
          html: `
            <div style="background:#dc2626; color:#ffffff; font-family:sans-serif; padding:4px 8px; border-radius:12px; font-size:10px; font-weight:900; box-shadow:0 4px 12px rgba(220,38,38,0.5); border:2px solid #ffffff; white-space:nowrap; display:flex; align-items:center; gap:4px;">
              <span>🚨 ${item.name}</span>
              <span style="background:#ffffff; color:#dc2626; padding:1px 5px; border-radius:8px;">+${item.needed} NEEDED</span>
            </div>
          `,
          iconSize: [130, 24],
          iconAnchor: [65, 12],
        });

        const marker = L.marker(latLng, { icon: customIcon });

        const popupContent = `
          <div style="font-family: sans-serif; padding: 6px; min-width: 220px;">
            <div style="font-size: 13px; font-weight: 900; color: #dc2626; margin-bottom: 4px;">
              🚨 ${item.name} Rescuer Deficit Alert
            </div>
            <div style="font-size: 11px; color: #334155; margin-bottom: 3px;">
              <strong>Active Season:</strong> ${this.getSeasonLabel(this.selectedSeason)}
            </div>
            <div style="font-size: 11px; color: #334155; margin-bottom: 3px;">
              <strong>Encounter Load:</strong> ${item.count.toLocaleString()} calls
            </div>
            <div style="font-size: 11px; color: #334155; margin-bottom: 3px;">
              <strong>Active Team:</strong> ${item.activeRescuers} Rescuers (${item.rescuesPerRescuer} rescues/rescuer/day)
            </div>
            <div style="font-size: 11px; font-weight: 800; color: #dc2626; margin-top: 6px; padding: 4px; background: #fee2e2; border-radius: 4px;">
              🎯 Immediate Deployment Required: +${item.needed} RRT Rescuers
            </div>
          </div>
        `;

        marker.bindPopup(popupContent);
        circle.bindPopup(popupContent);

        this.shortageLayerGroup.addLayer(circle);
        this.shortageLayerGroup.addLayer(marker);
      });

      if (boundsCoords.length > 0) {
        const bounds = L.latLngBounds(boundsCoords);
        if (bounds.isValid()) {
          this.mapObject.fitBounds(bounds, { padding: [60, 60], maxZoom: 10 });
        }
      }

      this.msg = `Displaying ${shortageItems.length} Rescuer Scarcity Zones for ${this.getSeasonLabel(this.selectedSeason)}`;
      this.showSnackBar = true;
    },

    async waitForMapTiles(timeout = 4000) {
      const mapNode = this.$refs.mapContainer || (this.mapObject && this.mapObject.getContainer());
      if (!mapNode) return;

      const tiles = Array.from(mapNode.querySelectorAll(".leaflet-tile"));
      if (tiles.length === 0) {
        await new Promise((resolve) => setTimeout(resolve, 250));
        return;
      }

      await Promise.race([
        Promise.all(
          tiles.map(
            (tile) =>
              new Promise((resolve) => {
                if (tile.complete && tile.naturalWidth > 0) {
                  resolve();
                  return;
                }
                tile.addEventListener("load", resolve, { once: true });
                tile.addEventListener("error", resolve, { once: true });
              })
          )
        ),
        new Promise((resolve) => setTimeout(resolve, timeout)),
      ]);
    },

    async buildMapImageExport() {
      if (!this.mapObject) {
        throw new Error("Map is not ready for export.");
      }

      this.mapObject.invalidateSize();
      await this.waitForMapTiles();
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      const mapNode = this.$refs.mapContainer || this.mapObject.getContainer();
      if (!mapNode) {
        throw new Error("Map container not found.");
      }

      const rect = mapNode.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const scale = Math.min(window.devicePixelRatio || 1, 2);
      const canvas = await html2canvas(mapNode, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
        scale,
        width,
        height,
        imageTimeout: 8000,
        ignoreElements: (element) => {
          if (!element || !element.classList) return false;
          return (
            element.classList.contains("leaflet-control-container") ||
            element.classList.contains("leaflet-popup-pane") ||
            element.classList.contains("leaflet-tooltip-pane")
          );
        },
        onclone: (clonedDocument) => {
          const clonedMap = clonedDocument.getElementById("map");
          if (!clonedMap) return;

          clonedMap.style.background = "#ffffff";
          clonedMap.querySelectorAll(".leaflet-control-container, .leaflet-popup-pane, .leaflet-tooltip-pane").forEach((node) => {
            node.remove();
          });
        },
      });

      return {
        dataUrl: canvas.toDataURL("image/png"),
        width,
        height,
      };
    },

    downloadDataUrl(filename, url) {
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    getExportFileBase() {
      const dateStr = new Date().toISOString().slice(0, 10);
      return `SARPA_Snake_Rescued_Map_${dateStr}`;
    },

    async withExportLoading(callback) {
      if (this.exportLoading) return;

      this.exportLoading = true;
      try {
        await callback();
      } catch (error) {
        console.error("Map export failed:", error);
        this.msg = "Map export failed. Please try again.";
        this.showSnackBar = true;
      } finally {
        this.exportLoading = false;
      }
    },

    exportMapPDF() {
      this.withExportLoading(async () => {
        this.msg = "Preparing map-only PDF export...";
        this.showSnackBar = true;

        const { dataUrl, width, height } = await this.buildMapImageExport();
        const exportWindow = window.open("", "_blank", "noopener,noreferrer,width=1200,height=900");
        if (!exportWindow) {
          throw new Error("Popup blocked");
        }

        const orientation = width > height ? "landscape" : "portrait";
        exportWindow.document.write(`
          <html>
            <head>
              <title>${this.getExportFileBase()}</title>
              <style>
                @page { size: ${orientation}; margin: 10mm; }
                html, body {
                  margin: 0;
                  padding: 0;
                  background: #ffffff;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                }
                img {
                  width: 100%;
                  height: auto;
                  display: block;
                }
              </style>
            </head>
            <body>
              <img src="${dataUrl}" alt="Snake rescued map export" />
            </body>
          </html>
        `);
        exportWindow.document.close();
        exportWindow.focus();
        setTimeout(() => {
          exportWindow.print();
        }, 400);
      });
    },

    exportMapPNG() {
      this.withExportLoading(async () => {
        this.msg = "Preparing map-only PNG export...";
        this.showSnackBar = true;
        const { dataUrl } = await this.buildMapImageExport();
        this.downloadDataUrl(`${this.getExportFileBase()}.png`, dataUrl);
      });
    },

    exportMapExcel() {
      this.withExportLoading(async () => {
        const records = this.isSelectionActive && this.selectedRecords.length > 0
          ? this.selectedRecords
          : this.data;

        const tableRows = records
          .map((item, index) => {
            const snake = item.snake && item.snake.name ? item.snake.name : "Unknown";
            const district = item.district || item.locationname || "-";
            const division = item.division || "-";
            const range = item.range || "-";
            const status = item.status || (item.rescue && item.rescue.status) || "-";
            const rescuer = item.user && (item.user.name || item.user.username)
              ? (item.user.name || item.user.username)
              : "-";
            const latitude = Array.isArray(item.location) ? item.location[1] : item.latitude || item.lat || "-";
            const longitude = Array.isArray(item.location) ? item.location[0] : item.longitude || item.lon || "-";

            return `
              <tr>
                <td>${index + 1}</td>
                <td>${this.formatDate(item.createdAt || item.date || item.rescuedate || "")}</td>
                <td>${snake}</td>
                <td>${district}</td>
                <td>${division}</td>
                <td>${range}</td>
                <td>${status}</td>
                <td>${rescuer}</td>
                <td>${latitude}</td>
                <td>${longitude}</td>
              </tr>
            `;
          })
          .join("");

        const html = `
          <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
            <head>
              <meta charset="utf-8" />
              <style>
                table { border-collapse: collapse; width: 100%; }
                th, td { border: 1px solid #cbd5e1; padding: 8px; font-size: 12px; }
                th { background: #dbeafe; font-weight: 700; }
              </style>
            </head>
            <body>
              <table>
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Date</th>
                    <th>Snake</th>
                    <th>District</th>
                    <th>Division</th>
                    <th>Range</th>
                    <th>Status</th>
                    <th>Rescuer</th>
                    <th>Latitude</th>
                    <th>Longitude</th>
                  </tr>
                </thead>
                <tbody>${tableRows}</tbody>
              </table>
            </body>
          </html>
        `;

        const blob = new Blob([html], { type: "application/vnd.ms-excel" });
        const url = URL.createObjectURL(blob);
        this.downloadDataUrl(`${this.getExportFileBase()}.xls`, url);
        setTimeout(() => URL.revokeObjectURL(url), 1000);

        this.msg = "Excel export generated from the current map dataset.";
        this.showSnackBar = true;
      });
    },

    getSeasonLabel(season) {
      if (season === "monsoon") return "South-West Monsoon";
      if (season === "post_monsoon") return "Post-Monsoon Breeding Window";
      if (season === "summer") return "Summer Dry Season";
      if (season === "current") return "Current Month Window";
      return "All-Year Window";
    },

    startLoadingTicker() {
      this.stopLoadingTicker();
      this.loadingMsgIndex = 0;
      this.loadingTimer = setInterval(() => {
        this.loadingMsgIndex = (this.loadingMsgIndex + 1) % this.loadingMessages.length;
      }, 700);
    },

    stopLoadingTicker() {
      if (this.loadingTimer) {
        clearInterval(this.loadingTimer);
        this.loadingTimer = null;
      }
    },
    initMap() {
      this.$nextTick(() => {
        const mapDom = document.getElementById("map");
        if (!mapDom) return;

        if (this.mapObject) {
          this.mapObject.remove();
          this.mapObject = null;
        }

        this.mapObject = L.map("map", {
          center: [10.8505, 76.2711],
          zoom: 7.5,
          zoomControl: false,
          scrollWheelZoom: false,
        });

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          crossOrigin: true,
        }).addTo(this.mapObject);

        L.control.zoom({ position: "bottomright" }).addTo(this.mapObject);

        this.drawnItems = new L.FeatureGroup();
        this.mapObject.addLayer(this.drawnItems);

        this.markersClusterGroup = L.markerClusterGroup({
          showCoverageOnHover: false,
          maxClusterRadius: 45,
          chunkedLoading: true,
          chunkInterval: 5000,
          chunkDelay: 1,
        });

        this.pointersLayer = L.layerGroup();

        this.drawControl = new L.Control.Draw({
          position: "topright",
          draw: {
            polygon: {
              allowIntersection: false,
              showArea: true,
              shapeOptions: { color: "#3f51b5", fillColor: "#3f51b5", fillOpacity: 0.2, weight: 2 },
            },
            rectangle: {
              shapeOptions: { color: "#3f51b5", fillColor: "#3f51b5", fillOpacity: 0.2, weight: 2 },
            },
            circle: false, polyline: false, marker: false, circlemarker: false,
          },
          edit: { featureGroup: this.drawnItems, remove: true },
        });

        if (this.showDrawControls) {
          this.mapObject.addControl(this.drawControl);
        }

        this.mapObject.on(L.Draw.Event.CREATED, (e) => {
          const layer = e.layer;
          this.drawnItems.clearLayers();
          this.drawnItems.addLayer(layer);
          this.processSelection(layer);
        });

        this.mapObject.on(L.Draw.Event.DELETED, () => {
          this.clearSelection();
        });

      });
    },

    renderMapData() {
      if (!this.mapObject) {
        setTimeout(() => {
          this.renderMapData();
        }, 100);
        return;
      }

      // Ensure layer groups are instantiated safely
      if (!this.markersClusterGroup) {
        this.markersClusterGroup = L.markerClusterGroup({
          showCoverageOnHover: false,
          maxClusterRadius: 45,
          chunkedLoading: true,
          chunkInterval: 5000,
          chunkDelay: 1,
        });
      } else {
        try {
          this.markersClusterGroup.clearLayers();
        } catch (err) {
          console.warn("Failed clearing markersClusterGroup:", err);
        }
      }

      if (!this.pointersLayer) {
        this.pointersLayer = L.layerGroup();
      } else {
        try {
          this.pointersLayer.clearLayers();
        } catch (err) {
          console.warn("Failed clearing pointersLayer:", err);
        }
      }

      if (this.heatmapLayer) {
        try {
          this.mapObject.removeLayer(this.heatmapLayer);
        } catch (err) {
          console.warn("Failed removing heatmapLayer:", err);
        }
        this.heatmapLayer = null;
      }

      try {
        if (this.mapObject.hasLayer(this.markersClusterGroup)) {
          this.mapObject.removeLayer(this.markersClusterGroup);
        }
        if (this.mapObject.hasLayer(this.pointersLayer)) {
          this.mapObject.removeLayer(this.pointersLayer);
        }
      } catch (err) {
        console.warn("Failed removing layers from map:", err);
      }

      if (!this.data || this.data.length === 0) {
        if (this.hasFetchedRecords) {
          this.appLoading = false;
        }
        return;
      }

      try {
        const canvasRenderer = L.canvas({ padding: 0.5 });
        const heatmapPoints = [];
        const pointerBatch = [];
        const clusterBatch = [];

        this.data.forEach((user) => {
          let lat = null;
          let lon = null;

          if (user.location) {
            if (Array.isArray(user.location) && user.location.length >= 2) {
              lon = Number(user.location[0]);
              lat = Number(user.location[1]);
            } else if (typeof user.location === "object" && Array.isArray(user.location.coordinates) && user.location.coordinates.length >= 2) {
              lon = Number(user.location.coordinates[0]);
              lat = Number(user.location.coordinates[1]);
            }
          }

          if (lat === null || lon === null || isNaN(lat) || isNaN(lon) || lat === 0 || lon === 0) {
            if (user.lat && user.lon) {
              lat = Number(user.lat);
              lon = Number(user.lon);
            } else if (user.latitude && user.longitude) {
              lat = Number(user.latitude);
              lon = Number(user.longitude);
            }
          }

          if (lat !== null && lon !== null && !isNaN(lat) && !isNaN(lon) && (lat !== 0 || lon !== 0)) {
            heatmapPoints.push([lat, lon, 1.0]);

            const sName = String((user.snake && user.snake.name) || "").toLowerCase();
            const vType = String((user.snake && user.snake.venomType) || "").toLowerCase();
            const isVenomous = vType.includes("highly") || vType.includes("high") || HIGHLY_VENOMOUS_SPECIES.has(sName);
            const riskScore = isVenomous ? 92 : 35;

            if (this.showPointers) {
              const marker = L.circleMarker([lat, lon], {
                renderer: canvasRenderer,
                radius: 6,
                fillColor: isVenomous ? "#e11d48" : "#3f51b5",
                color: "#ffffff",
                weight: 1,
                opacity: 0.9,
                fillOpacity: 0.8,
              });

              marker.on("click", (e) => {
                if (e && e.originalEvent) L.DomEvent.stopPropagation(e.originalEvent);
                this.selectedPointer = { ...user, isVenomous, riskScore };
              });
              pointerBatch.push(marker);
            }

            if (this.showHotspots) {
              const clusterMarker = L.marker([lat, lon]);
              clusterMarker.on("click", (e) => {
                if (e && e.originalEvent) L.DomEvent.stopPropagation(e.originalEvent);
                this.selectedPointer = { ...user, isVenomous, riskScore };
              });
              clusterBatch.push(clusterMarker);
            }
          }
        });

        if (this.showPointers && pointerBatch.length > 0) {
          pointerBatch.forEach((m) => this.pointersLayer.addLayer(m));
          this.mapObject.addLayer(this.pointersLayer);
        }

        if (this.showHotspots && clusterBatch.length > 0) {
          this.markersClusterGroup.addLayers(clusterBatch);
          this.mapObject.addLayer(this.markersClusterGroup);
        }

        if (this.showHeatmap && heatmapPoints.length > 0) {
          this.heatmapLayer = L.heatLayer(heatmapPoints, {
            radius: 25,
            blur: 15,
            maxZoom: 16,
          }).addTo(this.mapObject);
        }

        this.calculateSelectionStats();
      } catch (err) {
        console.error("Error rendering map data:", err);
      } finally {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              this.appLoading = false;
            }, 400);
          });
        });
      }
    },

    toggleMapMode() {
      this.renderMapData();
    },

    togglePanel(panelName) {
      this.activePanel = this.activePanel === panelName ? null : panelName;
    },

    toggleDrawControls() {
      this.showDrawControls = !this.showDrawControls;
    },

    toggleDistrictLayer() {
      if (!this.mapObject) return;
      if (this.gisLayers.districts) {
        if (!this.geoJsonData.districts) {
          axios.get("/alldata/formap", { headers: { "x-auth-token": localStorage.getItem("token") } }).then((res) => {
            if (res.data && res.data.status) {
              this.geoJsonData.districts = res.data.data;
              this.geoJsonLeafletLayers.districts = L.geoJSON(res.data.data, {
                style: { color: "#1e3a8a", weight: 2, fillOpacity: 0.05, dashArray: "4,4" },
              }).addTo(this.mapObject);
            }
          });
        } else if (this.geoJsonLeafletLayers.districts) {
          this.mapObject.addLayer(this.geoJsonLeafletLayers.districts);
        }
      } else if (this.geoJsonLeafletLayers.districts) {
        this.mapObject.removeLayer(this.geoJsonLeafletLayers.districts);
      }
    },
    toggleDivisionLayer() {
      if (!this.mapObject) return;
      if (this.gisLayers.divisions) {
        if (!this.geoJsonData.divisions) {
          axios.get("/shapes/divisions", { headers: { "x-auth-token": localStorage.getItem("token") } }).then((res) => {
            if (res.data && res.data.status) {
              this.geoJsonData.divisions = res.data.data;
              this.geoJsonLeafletLayers.divisions = L.geoJSON(res.data.data, {
                style: { color: "#3f51b5", weight: 1.5, fillOpacity: 0.08 },
              }).addTo(this.mapObject);
            }
          });
        } else if (this.geoJsonLeafletLayers.divisions) {
          this.mapObject.addLayer(this.geoJsonLeafletLayers.divisions);
        }
      } else if (this.geoJsonLeafletLayers.divisions) {
        this.mapObject.removeLayer(this.geoJsonLeafletLayers.divisions);
      }
    },
    toggleRangeLayer() {
      if (!this.mapObject) return;
      if (this.gisLayers.ranges) {
        if (!this.geoJsonData.ranges) {
          axios.get("/shapes/ranges", { headers: { "x-auth-token": localStorage.getItem("token") } }).then((res) => {
            if (res.data && res.data.status) {
              this.geoJsonData.ranges = res.data.data;
              this.geoJsonLeafletLayers.ranges = L.geoJSON(res.data.data, {
                style: { color: "#b45309", weight: 1.2, fillOpacity: 0.04 },
              }).addTo(this.mapObject);
            }
          });
        } else if (this.geoJsonLeafletLayers.ranges) {
          this.mapObject.addLayer(this.geoJsonLeafletLayers.ranges);
        }
      } else if (this.geoJsonLeafletLayers.ranges) {
        this.mapObject.removeLayer(this.geoJsonLeafletLayers.ranges);
      }
    },
    togglePASLayer() {
      if (!this.mapObject) return;
      if (this.gisLayers.pas) {
        if (!this.geoJsonData.pas) {
          axios.get("/shapes/pas/", { headers: { "x-auth-token": localStorage.getItem("token") } }).then((res) => {
            if (res.data && res.data.status) {
              this.geoJsonData.pas = res.data.data;
              this.geoJsonLeafletLayers.pas = L.geoJSON(res.data.data, {
                style: { color: "#7e22ce", weight: 2, fillOpacity: 0.15 },
              }).addTo(this.mapObject);
            }
          });
        } else if (this.geoJsonLeafletLayers.pas) {
          this.mapObject.addLayer(this.geoJsonLeafletLayers.pas);
        }
      } else if (this.geoJsonLeafletLayers.pas) {
        this.mapObject.removeLayer(this.geoJsonLeafletLayers.pas);
      }
    },

    toggleFullscreen() {
      const elem = this.$el.querySelector(".snake-map-dashboard") || this.$el;
      if (!elem) return;

      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        if (elem.requestFullscreen) elem.requestFullscreen();
        else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
      } else {
        if (document.exitFullscreen) document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      }
    },

    onFullscreenChange() {
      this.isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
      this.$nextTick(() => {
        if (this.mapObject) {
          setTimeout(() => this.mapObject.invalidateSize(), 150);
        }
      });
    },

    processSelection(layer) {
      const latlngs = layer.getLatLngs()[0];
      const selected = [];

      this.data.forEach((item) => {
        if (item.location && item.location.length === 2) {
          const lat = item.location[1];
          const lon = item.location[0];
          if (this.isPointInPolygon([lat, lon], latlngs)) {
            selected.push(item);
          }
        }
      });

      this.selectedRecords = selected;
      this.isSelectionActive = true;
      this.calculateSelectionStats();
    },

    isPointInPolygon(point, polygonCoords) {
      const x = point[0];
      const y = point[1];
      let inside = false;

      for (let i = 0, j = polygonCoords.length - 1; i < polygonCoords.length; j = i++) {
        const xi = polygonCoords[i].lat;
        const yi = polygonCoords[i].lng;
        const xj = polygonCoords[j].lat;
        const yj = polygonCoords[j].lng;

        const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
        if (intersect) inside = !inside;
      }
      return inside;
    },

    clearSelection() {
      this.selectedRecords = [];
      this.isSelectionActive = false;
      if (this.drawnItems) {
        this.drawnItems.clearLayers();
      }
      this.calculateSelectionStats();
    },

    calculateSelectionStats() {
      const records = this.isSelectionActive ? this.selectedRecords : this.data;

      const speciesMap = {};
      records.forEach((item) => {
        const rawName = item.snake && item.snake.name ? item.snake.name.trim() : "";
        if (rawName && !rawName.toLowerCase().includes("unidentified") && !rawName.toLowerCase().includes("unknown")) {
          speciesMap[rawName] = (speciesMap[rawName] || 0) + 1;
        }
      });

      this.selectionStats = Object.keys(speciesMap)
        .map((name) => ({ name, count: speciesMap[name] }))
        .sort((a, b) => b.count - a.count);
    },

    getSpeciesPercentage(count) {
      const statsArray = this.selectionStats;
      if (!statsArray || statsArray.length === 0) return 0;
      const maxCount = statsArray[0].count;
      return maxCount > 0 ? Math.round((count / maxCount) * 100) : 0;
    },

    save(fromdate) {
      this.fromdate = fromdate;
      this.menu = false;
    },
    saveto(todate) {
      this.todate = todate;
      this.menu2 = false;
    },

    getDivisionData() {
      axios({
        method: "GET",
        url: "/platform/division/getlistdropdown",
        headers: { token: localStorage.getItem("token") },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.divisionList = response.data.data;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.ServerError = true;
          console.error(err);
        });
    },

    getRangeData() {
      axios({
        method: "POST",
        url: "/platform/range/getlistdropdown",
        headers: { token: localStorage.getItem("token") },
        data: { division: this.divisionname },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.rangeList = response.data.data;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.ServerError = true;
          console.error(err);
        });
    },

    checkUpdate() {
      if (this.alldata === true) {
        this.timeperiod = false;
        this.bigsnakes = false;
        this.allsnakes = false;
        this.divisioncheck = false;
        this.users = false;
        this.districtcheck = false;

        const { from, to } = this.getDefaultDates();
        this.fromdate = from;
        this.todate = to;

        this.district = "";
        this.bigsnakeselect = [];
        this.allsnakelist = [];
        this.selecteduser = "";
        this.divisionname = "";
        this.rangename = "";

        this.find();
      }
    },

    reset() {
      this.timeperiod = false;
      this.bigsnakes = false;
      this.allsnakes = false;
      this.users = false;
      this.divisioncheck = false;
      this.districtcheck = false;
      this.alldata = true;
      this.checkUpdate();
    },

    getDefaultDates() {
      const today = new Date();
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(today.getMonth() - 6);
      return {
        from: sixMonthsAgo.toISOString().substring(0, 10),
        to: today.toISOString().substring(0, 10),
      };
    },

    getData() {
      axios({
        method: "GET",
        url: "/user/rescued/all",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: { keyword: this.searchUser },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.userslist = response.data.data;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.ServerError = true;
          console.error(err);
        });
    },

    find() {
      const params = {};
      if (this.timeperiod && this.fromdate && this.todate) {
        params.from = this.fromdate;
        params.to = this.todate;
      }
      if (this.bigsnakeselect && this.bigsnakeselect.length > 0) params.big4 = this.bigsnakeselect;
      if (this.allsnakelist && this.allsnakelist.length > 0) params.snakeid = this.allsnakelist;
      if (this.selecteduser) params.username = this.selecteduser;
      if (this.divisionname) params.division = this.divisionname;
      if (this.rangename) params.range = this.rangename;
      if (this.district) params.district = this.district;

      this.appLoading = true;
      this.hasFetchedRecords = false;
      axios({
        method: "POST",
        url: "/location/find",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: params,
      })
        .then((response) => {
          if (response.data.status === true) {
            this.data = response.data.data.records || [];
            this.totalcount = this.data.length;
            this.hasFetchedRecords = true;
            this.recomputeAllStats();
            this.clearSelection();
            this.renderMapData();
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.hasFetchedRecords = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.hasFetchedRecords = true;
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },

    formatDate(date) {
      if (!date) return "-";
      const [year, month, day] = date.slice(0, 10).split("-");
      return `${day}-${month}-${year}`;
    },

    getList() {
      axios({
        method: "GET",
        url: "/snake/all/",
        headers: { "x-auth-token": localStorage.getItem("token") },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.snakeitems = response.data.data;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.ServerError = true;
          console.error(err);
        });
    },
  },
};
</script>

<style scoped>
.snake-map-dashboard {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: #0f172a;
}

#map {
  width: 100%;
  height: 100%;
  z-index: 1;
}

.floating-title-banner {
  position: absolute;
  top: 18px;
  left: 20px;
  z-index: 999;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(63, 81, 181, 0.2);
  border-radius: 16px;
  padding: 10px 18px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.banner-main-title {
  font-size: 11px;
  font-weight: 800;
  color: #3f51b5;
  letter-spacing: 0.1em;
}

.banner-subtitle {
  font-size: 16px;
  font-weight: 900;
  color: #0f172a;
}

.floating-north-arrow {
  position: absolute;
  top: 18px;
  right: 20px;
  z-index: 999;
}

.map-floating-toolbar {
  position: absolute;
  top: 90px;
  left: 20px;
  z-index: 999;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.toolbar-btn {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12) !important;
}

.floating-overlay-card {
  position: absolute;
  top: 90px;
  left: 76px;
  z-index: 999;
  width: 380px;
  max-width: calc(100vw - 100px);
  max-height: calc(100vh - 180px);
  background: rgba(255, 255, 255, 0.96) !important;
  backdrop-filter: blur(12px);
  border-radius: 20px !important;
  border: 1px solid rgba(63, 81, 181, 0.2);
  padding: 18px;
  display: flex;
  flex-direction: column;
}

.panel-card-title {
  font-size: 16px;
  font-weight: 800;
}

.panel-card-subtitle {
  font-size: 11px;
  color: #64748b;
}

.stats-scroll-container {
  overflow-y: auto;
  max-height: 480px;
}

.predictive-metrics-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.metric-box {
  border-radius: 12px;
  padding: 10px;
  display: flex;
  flex-direction: column;
}

.bg-indigo-light { background: #e0e7ff; }
.bg-rose-light { background: #ffe4e6; }
.bg-amber-light { background: #fef3c7; }
.bg-blue-light { background: #dbeafe; }

.metric-box__label {
  font-size: 10px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
}

.metric-box__val {
  font-size: 20px;
  font-weight: 900;
  line-height: 1.1;
  margin-top: 2px;
}

.ai-field-recommendation {
  background: #e0e7ff;
  border: 1px solid #c7d2fe;
}

.predictive-pointer-inspector {
  position: absolute;
  bottom: 24px;
  left: 20px;
  z-index: 999;
  width: 420px;
  max-width: calc(100vw - 40px);
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(63, 81, 181, 0.2);
  border-radius: 20px;
  padding: 14px;
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.16);
}

.inspector-img-wrap {
  width: 64px;
  height: 64px;
  border-radius: 14px;
  overflow: hidden;
  background: #e2e8f0;
  flex-shrink: 0;
}

.inspector-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.inspector-img-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.inspector-name {
  font-size: 15px;
  font-weight: 800;
}

.floating-timeline-card {
  position: absolute;
  bottom: 24px;
  right: 20px;
  z-index: 999;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(63, 81, 181, 0.2);
  border-radius: 16px;
  padding: 10px 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.timeline-title {
  font-size: 10px;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
}

.timeline-dates {
  font-size: 13px;
  font-weight: 800;
  color: #3f51b5;
}

.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.25s ease;
}
.slide-fade-enter, .slide-fade-leave-to {
  transform: translateX(-15px);
  opacity: 0;
}

.slide-up-enter-active, .slide-up-leave-active {
  transition: all 0.25s ease;
}
.slide-up-enter, .slide-up-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

/* ANALYTICS LOADING OVERLAY STYLES */
.analytics-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 999999;
  background: rgba(15, 23, 42, 0.82);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.analytics-loader-card {
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(63, 81, 181, 0.25);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.35), 0 0 30px rgba(63, 81, 181, 0.15);
  border-radius: 20px;
  padding: 36px 32px;
  max-width: 440px;
  width: 100%;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.radar-pulse-container {
  position: relative;
  width: 90px;
  height: 90px;
  margin: 0 auto 20px auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loader-center-icon {
  z-index: 2;
  background: #eef2ff;
  border-radius: 50%;
  width: 58px;
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #3f51b5;
  box-shadow: 0 0 15px rgba(63, 81, 181, 0.3);
}

.radar-ring {
  position: absolute;
  border-radius: 50%;
  border: 2px solid #3f51b5;
  opacity: 0;
  animation: radarPulse 2s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
}

.ring-1 { width: 60px; height: 60px; animation-delay: 0s; }
.ring-2 { width: 80px; height: 80px; animation-delay: 0.5s; }
.ring-3 { width: 100px; height: 100px; animation-delay: 1s; }

@keyframes radarPulse {
  0% { transform: scale(0.6); opacity: 0.8; }
  100% { transform: scale(1.4); opacity: 0; }
}

.loader-heading {
  font-size: 15px;
  font-weight: 900;
  letter-spacing: 1.5px;
  color: #3f51b5;
  margin-bottom: 6px;
}

.loader-subtext {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 20px;
}

.loader-progress-wrap {
  width: 100%;
  height: 6px;
  background: #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 16px;
  position: relative;
}

.loader-progress-bar-officer {
  height: 100%;
  width: 45%;
  background: linear-gradient(90deg, #3f51b5, #6366f1, #06b6d4);
  border-radius: 10px;
  animation: progressSlide 1.6s infinite ease-in-out;
}

@keyframes progressSlide {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
}

.loader-status-ticker {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
}

.spinning-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% { transform: rotate(360deg); }
}

.loader-footer-hint {
  font-size: 10px;
  color: #94a3b8;
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.floating-scarcity-btn-container {
  position: absolute;
  top: 16px;
  right: 64px;
  z-index: 1000;
}

@keyframes scarcityPulse {
  0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7); }
  70% { box-shadow: 0 0 0 12px rgba(220, 38, 38, 0); }
  100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
}

.pulsate-scarcity {
  animation: scarcityPulse 1.8s infinite;
}

@media print {
  body, html {
    background: #ffffff !important;
  }
  .map-floating-toolbar,
  .floating-title-banner,
  .floating-north-arrow,
  .v-navigation-drawer,
  .v-app-bar,
  .v-footer,
  .v-btn,
  .v-snack,
  .leaflet-control-container {
    display: none !important;
  }
  .snake-map-dashboard {
    height: 100vh !important;
    width: 100vw !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    background: #ffffff !important;
    z-index: 99999 !important;
  }
  .floating-overlay-card {
    position: relative !important;
    top: auto !important;
    right: auto !important;
    bottom: auto !important;
    left: auto !important;
    max-height: none !important;
    box-shadow: none !important;
    border: 1px solid #94a3b8 !important;
    page-break-inside: avoid;
  }
}
</style>
